"use client";

import { useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../hooks/useAuth";
import { userAPI, postsAPI } from "../../../services/api";
import { getUserPlaceholderImage } from "../../../utils/imageUtils";
import UserAverageRating from "../../social/UserAverageRating";
import { USER_TYPES } from "../../../constants/userTypes";
import "./SocialSidebar.css";

const getCount = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined || value === "") continue;
    const count = Number(value);
    if (Number.isFinite(count)) return Math.max(0, count);
  }
  return 0;
};

const SocialSidebar = ({ currentUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { getProfileImageUrl, getUserType, user } = useAuth();

  const { t } = useTranslation();
  const fallbackName = currentUser?.name || t("socialSidebar.fallbackName");
  const [profileStats, setProfileStats] = useState(null);
  const [profileSubType, setProfileSubType] = useState(currentUser?.sub_type || null);
  const [profileSubTypeDisplayName, setProfileSubTypeDisplayName] = useState(currentUser?.sub_type_display_name || null);
  const [averageRating, setAverageRating] = useState(currentUser?.average_rating ?? 0);
  const [ratingsCount, setRatingsCount] = useState(currentUser?.ratings_count ?? 0);
  const [, setRatingLabel] = useState(currentUser?.rating_label ?? null);

  const postsCount = getCount(
    profileStats?.posts,
    currentUser?.posts_pagination?.total,
    currentUser?.stats?.posts,
    currentUser?.posts_count
  );
  const connectsCount = getCount(
    profileStats?.friends,
    currentUser?.friends_count,
    currentUser?.stats?.friends,
    currentUser?.connections_count,
    currentUser?.stats?.connects,
    currentUser?.following_count,
    currentUser?.followers_count
  );
  const isChannelPage = location.pathname.startsWith("/social/channels");


  // Get user type color
  const currentUserType = getUserType();
  const getUserTypeColor = (userType) => {
    const userTypeConfig = {
      'PET_GUARDIAN': '#8548A8', // Purple
      'VETERINARIAN': '#00D4B3', // Cyan
      'SERVICE_PROVIDER': '#D65745', // Orange
      'CLINIC_MODERATOR': '#2A8AD2', // Blue
      'FIRM': '#1B3A6B',           // Navy Blue
    };
    return userTypeConfig[userType] || '#8548A8'; // Default to purple
  };
  const userTypeColor = getUserTypeColor(currentUserType);

  // Get pet guardian subtype and display name
  // Check multiple possible locations for subtype data (profile API, user object, currentUser)
  const guardianSubType =
    profileSubType ||
    user?.sub_type ||
    user?.subType ||
    currentUser?.sub_type ||
    currentUser?.subType;

  const guardianSubTypeDisplayName =
    profileSubTypeDisplayName ||
    user?.sub_type_display_name ||
    user?.subTypeDisplayName ||
    currentUser?.sub_type_display_name ||
    currentUser?.subTypeDisplayName;

  // Get veterinarian subtype and display name (same fields, different user type)
  const vetSubType = guardianSubType; // Same data source, just different user type
  const vetSubTypeDisplayName = guardianSubTypeDisplayName; // Same data source

  const hasSubType = (currentUserType === USER_TYPES.PET_GUARDIAN && guardianSubType) ||
    (currentUserType === USER_TYPES.VETERINARIAN && vetSubType) ||
    (currentUserType === USER_TYPES.FIRM && guardianSubType);

  // Format subtype display name (localized when the sub_type is a known value,
  // otherwise use display_name, otherwise capitalize sub_type)
  const getSubTypeDisplay = () => {
    if (!hasSubType) return null;

    const subType = guardianSubType || vetSubType;

    // Localize known subtype values (parent, influencer, rescuer, clinic_owner, ...)
    if (subType) {
      const localized = t(`socialSidebar.subTypes.${subType}`, '');
      if (localized) return localized;
    }

    // Use display name if available
    if (guardianSubTypeDisplayName || vetSubTypeDisplayName) {
      return guardianSubTypeDisplayName || vetSubTypeDisplayName;
    }

    // Fallback: capitalize first letter of sub_type
    if (subType) {
      // Handle snake_case (e.g., clinic_owner -> Clinic Owner)
      return subType
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }
    return null;
  };

  const subTypeDisplay = getSubTypeDisplay();

  const handleNavigation = (path) => {
    navigate(path);
  };


  // Load fresh profile stats once on mount — only if not already available
  const fetchedProfileIdRef = useRef(null);
  useEffect(() => {
    const profileId = user?.id || user?.user_id;
    if (!profileId || fetchedProfileIdRef.current === profileId) return;
    fetchedProfileIdRef.current = profileId;

    // Check if we already have the needed data from auth context or props
    const existingFriends =
      user?.friends_count ??
      user?.stats?.friends ??
      currentUser?.friends_count ??
      currentUser?.stats?.friends ??
      null;

    const existingPosts =
      user?.posts_pagination?.total ??
      currentUser?.posts_pagination?.total ??
      user?.posts_count ??
      user?.stats?.posts ??
      currentUser?.posts_count ??
      currentUser?.stats?.posts ??
      null;

    const existingSubType =
      user?.sub_type ??
      currentUser?.sub_type ??
      null;

    const existingSubTypeDisplayName =
      user?.sub_type_display_name ??
      currentUser?.sub_type_display_name ??
      null;

    const existingAvgRating =
      user?.average_rating ??
      currentUser?.average_rating ??
      null;

    const existingRatingsCount =
      user?.ratings_count ??
      currentUser?.ratings_count ??
      null;

    const hasCounts =
      Number(existingFriends) > 0 ||
      Number(existingPosts) > 0;

    const hasProfileDetails =
      (existingSubType !== null || existingSubTypeDisplayName !== null || getUserType() !== 'PET_GUARDIAN') &&
      existingAvgRating !== null &&
      existingRatingsCount !== null;

    if (hasCounts && hasProfileDetails) {
      // Use existing data without making an API call
      setProfileStats({
        friends: getCount(existingFriends),
        posts: getCount(existingPosts),
      });
      if (existingSubType) setProfileSubType(existingSubType);
      if (existingSubTypeDisplayName) setProfileSubTypeDisplayName(existingSubTypeDisplayName);
      setAverageRating(existingAvgRating);
      setRatingsCount(existingRatingsCount);
      return;
    }

    const loadProfileStats = async () => {
      try {
        const response = await userAPI.getProfile();
        const payload = response.data?.data || response.data;
        if (!payload) {
          console.warn("SocialSidebar: Empty profile payload");
          return;
        }

        const friends =
          getCount(payload.friends_count, payload.stats?.friends);
        let posts =
          getCount(payload.posts_pagination?.total, payload.posts_count, payload.stats?.posts);

        // Try to get true total including channel posts
        try {
          const trueTotal = await postsAPI.getTotalPostsCount();
          if (trueTotal !== null && trueTotal > posts) {
            posts = trueTotal;
          }
        } catch {
          // Ignore: fall back to profile count
        }

        setProfileStats({
          friends,
          posts,
        });

        // Also store subtype data from profile API
        if (payload.sub_type) {
          setProfileSubType(payload.sub_type);
        }
        if (payload.sub_type_display_name) {
          setProfileSubTypeDisplayName(payload.sub_type_display_name);
        }

        // Store rating data from profile API
        let avgRating = payload.average_rating ?? payload.averageRating ?? 0;
        let countRating = payload.ratings_count ?? payload.ratingsCount ?? 0;

        // Ensure we have posts to calculate rating from if backend fails
        let postsData = payload.posts;

        // Fallback: If rating is missing/zero, check posts.
        // If posts are missing from payload, fetch them.
        if (avgRating === null || avgRating === 0 || countRating === 0) {
          if (!postsData || !Array.isArray(postsData) || postsData.length === 0) {
            try {
              // Fetch only if needed
              const postsResponse = await postsAPI.getMyPosts();
              const fetchedPosts = postsResponse.data?.data || postsResponse.data;
              if (Array.isArray(fetchedPosts)) {
                postsData = fetchedPosts;
              }
            } catch (err) {
              console.warn("SocialSidebar: Failed to fetch posts for rating calculation", err);
            }
          }

          // Now try calculation again with postsData
          if (postsData && Array.isArray(postsData) && postsData.length > 0) {
            const ratedPosts = postsData.filter(p =>
              (p.average_rating !== null && p.average_rating !== undefined && parseFloat(p.average_rating) > 0) ||
              (p.avg_rating !== null && p.avg_rating !== undefined && parseFloat(p.avg_rating) > 0)
            );

            if (ratedPosts.length > 0) {
              const totalRating = ratedPosts.reduce((sum, p) =>
                sum + (parseFloat(p.average_rating ?? p.avg_rating) || 0), 0
              );
              avgRating = totalRating / ratedPosts.length;
              countRating = ratedPosts.length;

            }
          }
        }

        setAverageRating(avgRating);
        setRatingsCount(countRating);
        setRatingLabel(payload.rating_label ?? payload.ratingLabel ?? null);
      } catch (error) {
        // Non-fatal: sidebar can fall back to existing currentUser stats
        console.warn("Failed to load sidebar profile stats:", error);
      }
    };

    loadProfileStats();
  }, [currentUser, getUserType, user]);



  return (
    <>
      {/* Desktop and Tablet Sidebar */}
      <div
        className={`social-sidebar-desktop${isChannelPage ? " channel-list-only" : ""}`}
      >
        {/* Profile Section */}
        {!isChannelPage && (
          <div className="profile-section-container">
            {/* Profile Card - Contains picture, name, username, posts, and connects */}
            <div className="profile-card" style={{}}>
              {/* Profile Info - Horizontal Layout */}
              <div className="profile-info-container">
                {/* Picture on the left */}
                <img
                  src={getProfileImageUrl() || getUserPlaceholderImage(user)}
                  alt={t("socialSidebar.profileImageAlt", { name: fallbackName })}
                  className="profile-picture"
                  style={{
                    border: `4px solid ${userTypeColor}`,
                  }}
                  onError={(e) => {
                    e.target.src = getUserPlaceholderImage(user);
                  }}
                />

                {/* Name and Username on the right */}
                <div className="profile-text-container">
                  <h3 className="profile-name">
                    {fallbackName}
                  </h3>

                  {/* Rating / New badge - same behavior as user profile */}
                  {profileSubType !== 'rescuer' &&
                    guardianSubType !== 'rescuer' &&
                    (averageRating !== null && averageRating !== undefined) && (
                      (averageRating && parseFloat(averageRating) > 0) ? (
                        <UserAverageRating
                          averageRating={averageRating}
                          ratingsCount={ratingsCount}
                          ratingLabel={null}
                          size="small"
                        />
                      ) : (
                        <span className="social-sidebar-new-badge">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                          </svg>
                          {t("userprofile.newUser") || "New"}
                        </span>
                      )
                    )}

                  {/* Subtype Tag - Between name and username */}
                  {subTypeDisplay && (
                    <span style={{
                      fontSize: '12px',
                      color: userTypeColor,
                      fontWeight: 600,
                      margin: '2px 0'
                    }}>
                      {subTypeDisplay}
                    </span>
                  )}

                  {/* Show rating under subtype if user IS a rescuer */}
                  {(profileSubType === 'rescuer' || guardianSubType === 'rescuer') &&
                    (averageRating !== null && averageRating !== undefined) && (
                      (averageRating && parseFloat(averageRating) > 0) ? (
                        <UserAverageRating
                          averageRating={averageRating}
                          ratingsCount={ratingsCount}
                          ratingLabel={null}
                          size="small"
                        />
                      ) : (
                        <span className="social-sidebar-new-badge">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                          </svg>
                          {t("userprofile.newUser") || "New"}
                        </span>
                      )
                    )}
                </div>
              </div>

              {/* Stats: Posts + unified Connects (mutual connections/friends) */}
              <div className="profile-stats-container">
                <div className="profile-stat-item">
                  <p className="profile-stat-number">
                    {postsCount}
                  </p>
                  <span className="profile-stat-label">
                    {t("sideBar.Posts")}
                  </span>
                </div>

                <div className="profile-stat-item">
                  <p className="profile-stat-number">
                    {connectsCount}
                  </p>
                  <span className="profile-stat-label">
                    {t("sideBar.following")}
                  </span>
                </div>
              </div>
            </div>

            {/* Actions Card - Contains My Profile and My Pets buttons */}
            <div className="actions-card" style={{ marginTop: "16px" }}>
              {/* Profile Button */}
              <button
                onClick={() => handleNavigation(`/social/profile`)}
                className="action-button"
                style={{
                  backgroundColor: userTypeColor,
                  border: 'none',
                  color: '#FFFFFF',
                  fontWeight: 700,
                  fontSize: '16px',
                  height: '48px',
                  padding: '14px 20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                }}
              >
                <svg width="18" height="18" viewBox="260 260 640 640" fill="currentColor">
                  <path d="M577.91,326.24c151.74-12.32,283.78,101.66,295.88,251.57,12.19,151.02-100.89,283.78-251.68,295.96-150.55,12.16-283.14-100.37-295.84-250.85-12.69-150.39,99.67-284.35,251.63-296.68ZM598.8,410.38c-49.06.65-87.1,41.36-85.76,89.35,1.33,47.51,40.77,85.43,88.58,84.58,47.79-.85,86.3-40.48,85.35-88.67-.93-47.09-39.51-85.89-88.17-85.25ZM717.81,772.24c34.67-21.97,41.94-71.08,15.51-101.51-70.34-80.96-197.23-80.71-267.02.62-27.61,32.17-17,81.71,18.96,103.09,71.73,42.66,161.28,42.97,232.55-2.2Z"/>
                </svg>
                {t("sideBar.myProfile")}
              </button>

              {/* My Pets Button */}
              <button
                onClick={() => handleNavigation(`/dashboard/pets`)}
                className="action-button"
                style={{
                  backgroundColor: userTypeColor,
                  border: 'none',
                  color: '#FFFFFF',
                  fontWeight: 700,
                  fontSize: '16px',
                  height: '48px',
                  padding: '14px 20px',
                }}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6.95858 2.50008C7.94192 2.35842 8.98358 3.43342 9.28358 4.91675C9.58358 6.39175 9.04191 7.70842 8.05858 7.85842C7.08358 8.00842 6.03358 6.93342 5.72525 5.45008C5.41691 3.97508 5.97525 2.65842 6.95858 2.50008ZM12.9169 2.50008C13.9086 2.65842 14.4586 3.97508 14.1669 5.45008C13.8502 6.93342 12.8086 8.00842 11.8252 7.85842C10.8336 7.70842 10.2919 6.39175 10.6002 4.91675C10.9002 3.43342 11.9419 2.35842 12.9169 2.50008ZM2.50025 6.33342C3.45025 5.92508 4.74191 6.66675 5.41691 7.95842C6.05025 9.27508 5.83358 10.6584 4.89191 11.0668C3.95025 11.4751 2.66691 10.7418 2.00858 9.43342C1.35025 8.12508 1.58358 6.73342 2.50025 6.33342ZM17.5002 6.33342C18.4169 6.73342 18.6502 8.12508 17.9919 9.43342C17.3336 10.7418 16.0502 11.4751 15.1086 11.0668C14.1669 10.6584 13.9502 9.27508 14.5836 7.95842C15.2586 6.66675 16.5502 5.92508 17.5002 6.33342ZM16.1086 15.3168C16.1419 16.1001 15.5419 16.9668 14.8252 17.2918C13.3336 17.9751 11.5669 16.5584 9.90858 16.5584C8.25025 16.5584 6.46691 18.0334 5.00025 17.2918C4.16691 16.8834 3.59191 15.8001 3.70025 14.9001C3.85025 13.6584 5.34191 12.9918 6.22525 12.0834C7.40025 10.9084 8.23358 8.70008 9.90858 8.70008C11.5752 8.70008 12.4586 10.8751 13.5836 12.0834C14.5086 13.1001 16.0502 13.9584 16.1086 15.3168Z"
                    fill="#FFFFFF"
                  />
                </svg>
                {t("navbar.My_Pets")}
              </button>

              {/* Communities Button */}
              {!isChannelPage && (
                <button
                  onClick={() => handleNavigation("/social/channels")}
                  className="action-button"
                  style={{
                    backgroundColor: userTypeColor,
                    border: 'none',
                    color: '#FFFFFF',
                    fontWeight: 700,
                    fontSize: '16px',
                    height: '48px',
                    padding: '14px 20px',
                  }}
                >
                  <svg width="18" height="18" viewBox="240 240 720 720" fill="currentColor">
                    <path d="M470.45,869.79h-128.35c-5.62-3.07-11.71-7.1-11.7-14.8l.28-133.66c.05-23.44,18.83-40.8,40.17-45.98,34.46,30.71,85.7,30.61,119.96-.23,8.76,2.68,16.84,6.57,23.83,12.36l5.07,12.18c-31.55,12.47-51.81,42.16-51.77,76.41l.09,80.04c0,4.74,1.52,9.55,2.42,13.68Z" />
                    <path d="M869.6,717.08v140.96c-2.6,5.42-6.33,9.18-11.76,11.76h-128.35c1.57-6.02,2.53-12.22,2.52-18.99l-.11-75.77c-.05-33.64-20.3-62.75-51.51-75.36l4.89-11.98c6.94-6.36,15.1-9.68,23.9-12.54,34.37,31.08,86.02,30.73,120.2-.06l7.02,2.58c18.02,6.62,29.07,21.38,33.2,39.41Z" />
                    <path d="M688.31,869.79h-176.68c-5.79-2.77-11.99-7.09-11.99-14.7l-.03-80.06c0-20.58,12.62-38.41,31.4-45.8l8.77-3.45c34.49,31.34,86.47,30.83,120.66-.11l8.45,3.45c18.78,7.67,31.41,25.17,31.43,45.93l.09,77.88c0,8.46-4.86,13.89-12.1,16.87Z" />
                    <path d="M724.35,447.13l-113.28,113.42c-6.2,6.21-16.02,6.18-22.21-.01l-113-113.05c-27.23-27.24-25.82-71.32.99-97.35,27.81-27,70.82-25.59,98.42,2.07l24.7,24.74,28.05-27.75c26.89-26.6,69.94-24.59,96.1,1.99,25.45,25.86,27.04,69.11.23,95.96Z" />
                    <path d="M761.11,551.24c32.79-4.57,61.5,18.57,65.58,50.19,4.04,31.29-18.12,60.34-49.45,64.75-31.69,4.45-61.23-17.62-65.65-49.55-4.29-30.96,16.89-60.84,49.51-65.39Z" />
                    <path d="M421.96,551.27c32.43-4.93,61.31,17.49,66.1,48.52s-17.06,61.57-48.93,66.28c-31.21,4.61-60.78-16.81-65.78-48.34-4.93-31.05,15.9-61.49,48.61-66.46Z" />
                    <path d="M593.61,601.76c32.58-3.58,60.35,20.05,63.94,50.97,3.71,32-19.38,60.68-50.87,64.36s-60.41-18.93-64.37-50.27c-4.02-31.78,18.27-61.42,51.3-65.05Z" />
                    <path d="M810.39,377.26c-8.36,4.17-17.52.55-21.31-6.69-3.86-7.38-1.33-17.22,6.55-21.19l33.57-16.91c8.19-4.12,17.71-1.17,21.75,6.82,3.84,7.61.87,17.29-7.35,21.4l-33.2,16.58Z" />
                    <path d="M403.99,349.16c8.28,4.15,10.69,13.97,6.85,21.42-3.7,7.17-12.96,10.82-20.85,6.86l-34.37-17.22c-7.9-3.96-10.23-13.86-6.63-20.95,3.79-7.46,13.11-11.09,21.02-7.13l33.99,17.02Z" />
                    <path d="M842.97,467.02c8.19,4.05,11.49,12.47,8.67,20.19-2.83,7.72-12.49,13.09-20.65,9.05l-35.09-17.38c-7.76-3.85-10.46-13.11-7.3-20.36,3.39-7.78,12.91-12.02,21.04-8l33.34,16.51Z" />
                    <path d="M369.87,495.93c-8.21,4.09-17.38-.06-20.96-7.3-3.63-7.34-.98-16.99,7.03-20.98l34.37-17.14c7.8-3.89,17.03-.08,20.7,7.31,3.6,7.25,1.08,17.07-6.97,21.08l-34.17,17.04Z" />
                  </svg>
                  {t("sideBar.communities")}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Mobile Sidebar - Hidden by default, shown via toggle */}
      <div
        className="social-sidebar-mobile"
        style={{
          display: "none",
          position: "fixed",
          top: "240px",
          left: "-100%",
          width: "280px",
          height: "calc(100vh - 240px)",
          background: "#FFFFFF",
          borderRight: "1px solid #eee",
          padding: "20px",
          overflowY: "auto",
          fontFamily: '"Red Hat Display", sans-serif',
          zIndex: 200,
          transition: "left 0.3s ease",
        }}
      >
        {/* Mobile Profile Section */}
        {!isChannelPage && (
          <div className="profile-section-container-mobile">
            {/* Profile Card - Contains picture, name, username, posts, and connects */}
            <div className="profile-card" style={{ borderColor: userTypeColor }}>
              {/* Profile Info - Horizontal Layout */}
              <div className="profile-info-container">
                {/* Picture on the left */}
                <img
                  src={getProfileImageUrl() || getUserPlaceholderImage(user)}
                  alt={t("socialSidebar.profileImageAlt", { name: fallbackName })}
                  className="profile-picture"
                  style={{
                    border: `4px solid ${userTypeColor}`,
                  }}
                  onError={(e) => {
                    e.target.src = getUserPlaceholderImage(user);
                  }}
                />

                {/* Name and Username on the right */}
                <div className="profile-text-container">
                  <h3 className="profile-name-mobile">
                    {fallbackName}
                  </h3>

                  {/* Subtype Tag - Between name and username */}
                  {subTypeDisplay && (
                    <span style={{
                      fontSize: '12px',
                      color: userTypeColor,
                      fontWeight: 600,
                      margin: '2px 0'
                    }}>
                      {subTypeDisplay}
                    </span>
                  )}
                </div>
              </div>

              {/* Stats: Posts + unified Connects (mutual connections/friends) */}
              <div className="profile-stats-container">
                <div className="profile-stat-item">
                  <p className="profile-stat-number-mobile">
                    {postsCount}
                  </p>
                  <span className="profile-stat-label-mobile">
                    {t("sideBar.Posts")}
                  </span>
                </div>

                <div className="profile-stat-item">
                  <p className="profile-stat-number-mobile">
                    {connectsCount}
                  </p>
                  <span className="profile-stat-label-mobile">
                    {t("sideBar.following")}
                  </span>
                </div>
              </div>
            </div>

            {/* Actions Card - Contains My Profile and My Pets buttons */}
            <div className="actions-card" style={{ marginTop: "16px" }}>
              {/* Profile Button */}
              <button
                onClick={() => handleNavigation(`/social/profile`)}
                className="action-button"
                style={{
                  backgroundColor: userTypeColor,
                  border: 'none',
                  color: '#FFFFFF',
                  fontWeight: 700,
                  fontSize: '16px',
                  height: '48px',
                  padding: '14px 20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                }}
              >
                <svg width="18" height="18" viewBox="260 260 640 640" fill="currentColor">
                  <path d="M577.91,326.24c151.74-12.32,283.78,101.66,295.88,251.57,12.19,151.02-100.89,283.78-251.68,295.96-150.55,12.16-283.14-100.37-295.84-250.85-12.69-150.39,99.67-284.35,251.63-296.68ZM598.8,410.38c-49.06.65-87.1,41.36-85.76,89.35,1.33,47.51,40.77,85.43,88.58,84.58,47.79-.85,86.3-40.48,85.35-88.67-.93-47.09-39.51-85.89-88.17-85.25ZM717.81,772.24c34.67-21.97,41.94-71.08,15.51-101.51-70.34-80.96-197.23-80.71-267.02.62-27.61,32.17-17,81.71,18.96,103.09,71.73,42.66,161.28,42.97,232.55-2.2Z"/>
                </svg>
                {t("sideBar.myProfile")}
              </button>

              {/* My Pets Button */}
              <button
                onClick={() => handleNavigation(`/dashboard/pets`)}
                className="action-button"
                style={{
                  backgroundColor: userTypeColor,
                  border: 'none',
                  color: '#FFFFFF',
                  fontWeight: 700,
                  fontSize: '16px',
                  height: '48px',
                  padding: '14px 20px',
                }}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6.95858 2.50008C7.94192 2.35842 8.98358 3.43342 9.28358 4.91675C9.58358 6.39175 9.04191 7.70842 8.05858 7.85842C7.08358 8.00842 6.03358 6.93342 5.72525 5.45008C5.41691 3.97508 5.97525 2.65842 6.95858 2.50008ZM12.9169 2.50008C13.9086 2.65842 14.4586 3.97508 14.1669 5.45008C13.8502 6.93342 12.8086 8.00842 11.8252 7.85842C10.8336 7.70842 10.2919 6.39175 10.6002 4.91675C10.9002 3.43342 11.9419 2.35842 12.9169 2.50008ZM2.50025 6.33342C3.45025 5.92508 4.74191 6.66675 5.41691 7.95842C6.05025 9.27508 5.83358 10.6584 4.89191 11.0668C3.95025 11.4751 2.66691 10.7418 2.00858 9.43342C1.35025 8.12508 1.58358 6.73342 2.50025 6.33342ZM17.5002 6.33342C18.4169 6.73342 18.6502 8.12508 17.9919 9.43342C17.3336 10.7418 16.0502 11.4751 15.1086 11.0668C14.1669 10.6584 13.9502 9.27508 14.5836 7.95842C15.2586 6.66675 16.5502 5.92508 17.5002 6.33342ZM16.1086 15.3168C16.1419 16.1001 15.5419 16.9668 14.8252 17.2918C13.3336 17.9751 11.5669 16.5584 9.90858 16.5584C8.25025 16.5584 6.46691 18.0334 5.00025 17.2918C4.16691 16.8834 3.59191 15.8001 3.70025 14.9001C3.85025 13.6584 5.34191 12.9918 6.22525 12.0834C7.40025 10.9084 8.23358 8.70008 9.90858 8.70008C11.5752 8.70008 12.4586 10.8751 13.5836 12.0834C14.5086 13.1001 16.0502 13.9584 16.1086 15.3168Z"
                    fill="#FFFFFF"
                  />
                </svg>
                {t("navbar.My_Pets")}
              </button>

              {/* Communities Button */}
              {!isChannelPage && (
                <button
                  onClick={() => handleNavigation("/social/channels")}
                  className="action-button"
                  style={{
                    backgroundColor: userTypeColor,
                    border: 'none',
                    color: '#FFFFFF',
                    fontWeight: 700,
                    fontSize: '16px',
                    height: '48px',
                    padding: '14px 20px',
                  }}
                >
                  <svg width="18" height="18" viewBox="240 240 720 720" fill="currentColor">
                    <path d="M470.45,869.79h-128.35c-5.62-3.07-11.71-7.1-11.7-14.8l.28-133.66c.05-23.44,18.83-40.8,40.17-45.98,34.46,30.71,85.7,30.61,119.96-.23,8.76,2.68,16.84,6.57,23.83,12.36l5.07,12.18c-31.55,12.47-51.81,42.16-51.77,76.41l.09,80.04c0,4.74,1.52,9.55,2.42,13.68Z" />
                    <path d="M869.6,717.08v140.96c-2.6,5.42-6.33,9.18-11.76,11.76h-128.35c1.57-6.02,2.53-12.22,2.52-18.99l-.11-75.77c-.05-33.64-20.3-62.75-51.51-75.36l4.89-11.98c6.94-6.36,15.1-9.68,23.9-12.54,34.37,31.08,86.02,30.73,120.2-.06l7.02,2.58c18.02,6.62,29.07,21.38,33.2,39.41Z" />
                    <path d="M688.31,869.79h-176.68c-5.79-2.77-11.99-7.09-11.99-14.7l-.03-80.06c0-20.58,12.62-38.41,31.4-45.8l8.77-3.45c34.49,31.34,86.47,30.83,120.66-.11l8.45,3.45c18.78,7.67,31.41,25.17,31.43,45.93l.09,77.88c0,8.46-4.86,13.89-12.1,16.87Z" />
                    <path d="M724.35,447.13l-113.28,113.42c-6.2,6.21-16.02,6.18-22.21-.01l-113-113.05c-27.23-27.24-25.82-71.32.99-97.35,27.81-27,70.82-25.59,98.42,2.07l24.7,24.74,28.05-27.75c26.89-26.6,69.94-24.59,96.1,1.99,25.45,25.86,27.04,69.11.23,95.96Z" />
                    <path d="M761.11,551.24c32.79-4.57,61.5,18.57,65.58,50.19,4.04,31.29-18.12,60.34-49.45,64.75-31.69,4.45-61.23-17.62-65.65-49.55-4.29-30.96,16.89-60.84,49.51-65.39Z" />
                    <path d="M421.96,551.27c32.43-4.93,61.31,17.49,66.1,48.52s-17.06,61.57-48.93,66.28c-31.21,4.61-60.78-16.81-65.78-48.34-4.93-31.05,15.9-61.49,48.61-66.46Z" />
                    <path d="M593.61,601.76c32.58-3.58,60.35,20.05,63.94,50.97,3.71,32-19.38,60.68-50.87,64.36s-60.41-18.93-64.37-50.27c-4.02-31.78,18.27-61.42,51.3-65.05Z" />
                    <path d="M810.39,377.26c-8.36,4.17-17.52.55-21.31-6.69-3.86-7.38-1.33-17.22,6.55-21.19l33.57-16.91c8.19-4.12,17.71-1.17,21.75,6.82,3.84,7.61.87,17.29-7.35,21.4l-33.2,16.58Z" />
                    <path d="M403.99,349.16c8.28,4.15,10.69,13.97,6.85,21.42-3.7,7.17-12.96,10.82-20.85,6.86l-34.37-17.22c-7.9-3.96-10.23-13.86-6.63-20.95,3.79-7.46,13.11-11.09,21.02-7.13l33.99,17.02Z" />
                    <path d="M842.97,467.02c8.19,4.05,11.49,12.47,8.67,20.19-2.83,7.72-12.49,13.09-20.65,9.05l-35.09-17.38c-7.76-3.85-10.46-13.11-7.3-20.36,3.39-7.78,12.91-12.02,21.04-8l33.34,16.51Z" />
                    <path d="M369.87,495.93c-8.21,4.09-17.38-.06-20.96-7.3-3.63-7.34-.98-16.99,7.03-20.98l34.37-17.14c7.8-3.89,17.03-.08,20.7,7.31,3.6,7.25,1.08,17.07-6.97,21.08l-34.17,17.04Z" />
                  </svg>
                  {t("sideBar.communities")}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default SocialSidebar;
